import { Coords, Rect, Size, ConnectorAnchor, View } from 'src/types';
import { CoordsUtils } from 'src/utils/CoordsUtils';
import { getItemByIdOrThrow } from 'src/utils';
import { CONNECTOR_SEARCH_OFFSET } from 'src/config';
import { getBoundingBox, sortByPosition } from '@core/grid/gridUtils';
import { ConnectionPath } from './types';

/**
 * Core connection utilities
 * Pure TypeScript functions for connection calculations
 */

/**
 * Normalize a position relative to an origin point
 * 
 * @param position - Position to normalize
 * @param origin - Origin point
 * @returns Normalized coordinates
 */
export const normalizePositionFromOrigin = (
    position: Coords,
    origin: Coords
): Coords => {
    return CoordsUtils.subtract(origin, position);
};

/**
 * Convert a connector path tile from local to global coordinates
 * 
 * @param tile - Local tile coordinates
 * @param origin - Origin point of the path
 * @returns Global coordinates
 */
export const connectorPathTileToGlobal = (
    tile: Coords,
    origin: Coords
): Coords => {
    return CoordsUtils.subtract(origin, tile);
    // return CoordsUtils.subtract(origin, CoordsUtils.subtract(CoordsUtils.zero(), tile));
};

/**
 * Get all anchors from a list of connectors
 * 
 * @param connectors - Array of connectors
 * @returns Array of all anchors
 */
export const getAllAnchors = (connectors: any[]): ConnectorAnchor[] => {
    return connectors.reduce((acc, connector) => {
        return [...acc, ...connector.anchors];
    }, [] as ConnectorAnchor[]);
};

/**
 * Get the tile position of an anchor
 * Resolves anchor references (item, tile, or nested anchor)
 * 
 * @param anchor - Connector anchor
 * @param view - Current view containing items and connectors
 * @returns Tile coordinates
 */
export const getAnchorTile = (anchor: ConnectorAnchor, view: View): Coords => {
    // If anchor references an item, return item's tile
    if (anchor.ref.item) {
        const viewItem = getItemByIdOrThrow(view.items, anchor.ref.item).value;
        return viewItem.tile;
    }

    // If anchor references another anchor, resolve recursively
    if (anchor.ref.anchor) {
        const allAnchors = getAllAnchors(view.connectors ?? []);
        const nextAnchor = getItemByIdOrThrow(allAnchors, anchor.ref.anchor).value;
        return getAnchorTile(nextAnchor, view);
    }

    // If anchor has direct tile coordinates, return them
    if (anchor.ref.tile) {
        return anchor.ref.tile;
    }

    throw new Error('Could not get anchor tile.');
};

/**
 * Calculate the path for a connector based on its anchors
 * Uses pathfinding algorithm to route between anchor points
 * 
 * @param anchors - Array of connector anchors
 * @param view - Current view
 * @returns Connection path with tiles and bounding rectangle
 */
export const getConnectorPath = ({
    anchors,
    view
}: {
    anchors: ConnectorAnchor[];
    view: View;
}): ConnectionPath => {
    if (anchors.length < 2) {
        throw new Error(
            `Connector needs at least two anchors (received: ${anchors.length})`
        );
    }

    // Get tile positions for all anchors
    const anchorPositions = anchors.map((anchor) => {
        return getAnchorTile(anchor, view);
    });

    // Calculate search area with offset for pathfinding
    const searchArea = getBoundingBox(anchorPositions, CONNECTOR_SEARCH_OFFSET);
    const sorted = sortByPosition(searchArea);

    // Define the bounding rectangle
    const rectangle: Rect = {
        from: { x: sorted.highX, y: sorted.highY },
        to: { x: sorted.lowX, y: sorted.lowY }
    };

    // Normalize anchor positions relative to search area origin
    const normalizedPositions = anchorPositions.map((position) => {
        return normalizePositionFromOrigin(position, rectangle.from);
    });

    // A connector with only its two endpoint anchors has no user-defined
    // bends. Drawing the endpoints directly gives it a stable straight line
    // in the isometric projection. Running square-grid A* here can produce a
    // technically short grid route whose diagonal/axis ordering projects as
    // a large, side-switching dogleg when either item moves.
    const tiles = normalizedPositions;

    return { tiles, rectangle };
};

/**
 * Get a rectangle from a starting position and size
 * 
 * @param from - Starting coordinates
 * @param size - Size of the rectangle
 * @returns Rectangle with from and to coordinates
 */
export const getRectangleFromSize = (from: Coords, size: Size): Rect => {
    return {
        from,
        to: { x: from.x + size.width, y: from.y + size.height }
    };
};
